import { Injectable } from '@angular/core';
import { LoadNeedsphereService } from './load-needsphere.service';
import { LoadConfigService } from './load-config.service';

import { UtilsService } from './utils.service';
import { StudyNavigationService } from './study-navigation.service';

@Injectable({
  providedIn: 'root',
})
export class LoadStimuliService {
  private config: Object;
  private defaultLng: string;
  constructor(
    private loadNeeds: LoadNeedsphereService,
    private loadConfigService: LoadConfigService,
    private utils: UtilsService,
    private navigationService: StudyNavigationService
  ) {
    this.config = this.loadConfigService.getConfig();
    this.defaultLng = this.loadConfigService.getDefaultLanguage();
  }

  // TODO: remove parameters 'readingMode' & 'practiceMode' (are probably used in test-display currently)
  getStimuli(
    IATtype: string,
    readingMode: boolean,
    practiceMode: boolean,
    IATmode: string,
    lng?: string
  ) {
    if (IATmode === 'reading') {
      switch (IATtype) {
        case 'persona':
          return this.loadNeeds.getNeedsArray(lng);
        case 'brand':
          return this.loadNeeds.getBrandNeedsArray(lng);
        case 'modular':
          return this.loadNeeds.getModularNeedsArray(lng);
      }
    }

    if (IATmode === 'practice') {
      if (IATtype === 'conjoint') {
        return this.getProfilesDemo(lng);
      } else {
        return this.loadNeeds.getNeedsDemo(lng);
      }
    }

    if (IATtype === 'agreement') {
      return this.loadConfigService.getStatements(lng ? lng : 'EN');
    } else if (
      IATtype === 'IAT' ||
      IATtype === 'priming' ||
      IATtype === 'conjoint'
    ) {
      // LOAD IAT/priming STIMULI
      return this.createValueTypeArray(lng, IATtype);
    }

    return this.loadNeeds.getNeedsWithRepetitions(IATtype, lng);
  }

  getProfilesDemo(lng: string = this.defaultLng): string[] {
    const demoProfiles = this.config['demoProfiles'];
    const translatedDemoNeeds = demoProfiles[lng]['profiles'].slice();
    return translatedDemoNeeds;
  }

  private parseStimuliArray(stimuliConfig: any, lng: string) {
    return stimuliConfig.stimuli[lng];
  }

  private parseStimuliRepetitions(stimuliConfig: any, lng: string) {
    return stimuliConfig.stimuliRepetitions[lng];
  }

  /**
   * This function parses client_config.json for the current test to get all required information in the required language
   * and creates a shuffled value type array
   * @param lng string which is used to parse the correct data in client_config.json
   * @returns array of {value, type} objects without consecutive duplicates
   */
  private createValueTypeArray(lng: string, testType: string) {
    const studies = this.navigationService.getCurrentTestConfig();
    const stimuliArray = this.parseStimuliArray(studies, lng);

    if (testType === 'priming') {
      return studies['stimuli'][lng];
    }
    if (testType === 'conjoint') {
      return studies['stimuli'][lng]['profiles'];
    }
    const stimuliRepetitions = this.parseStimuliRepetitions(studies, lng);
    // if test type IAT
    let valueTypeArray = [];
    let shuffledValueTypeArray = [];

    valueTypeArray = this.transformStimuli(stimuliArray, stimuliRepetitions);
    shuffledValueTypeArray = this.shuffleStimuli(valueTypeArray);

    return shuffledValueTypeArray;
  }

  /**
   * Returns an array with {value, type} stimuli objects
   * @param stimuliArray dict of type and an array of values - "positive": ["Glücklich", "Frieden", "Liebe", "Lachen", "Freude"]
   * @param stimuliRepetitions dict of type and corresponding repetitions for the type - "positive": 10
   * @returns array of {value, type} objects
   */
  transformStimuli(stimuliArray: any, stimuliRepetitions: any) {
    let buildArray = [];
    Object.keys(stimuliArray).map((stimulusType) => {
      let helperArray = stimuliArray[stimulusType].map((stimulus) => {
        let stimulusObject = {
          value: stimulus,
          type: stimulusType,
        };
        return stimulusObject;
      });

      // check how often the stimuli should be repeated for each type according to the config (see stimuliRepetitions)
      let numberOfStimuli = Object.keys(stimuliArray[stimulusType]).length;
      let numberOfOccurrences = stimuliRepetitions[stimulusType];

      let integerQuotient: number = Math.floor(
        numberOfOccurrences / numberOfStimuli
      );
      let remainder: number = numberOfOccurrences % numberOfStimuli;
      let tmpArray = [];

      // duplicate the stimuli array as often as needed (quotient number of times)
      for (let iterator = 0; iterator < integerQuotient; iterator++) {
        tmpArray = tmpArray.concat(helperArray);
      }

      // if there is a remainder, add as many random elements as necessary
      for (let iterator = 0; iterator < remainder; iterator++) {
        let randomIndex = Math.floor(Math.random() * helperArray.length);
        let tmp = helperArray.splice(randomIndex, 1);
        tmpArray = tmpArray.concat(tmp);
      }

      buildArray = buildArray.concat(tmpArray);
    });
    return buildArray;
  }

  /**
   * Returns an array with {value, type} stimuli objects which is shuffled
   * @param valueTypeArray array with {value, type} stimuli - unshuffled
   * @returns array of {value, type} objects without consecutive duplicates
   */
  shuffleStimuli(valueTypeArray) {
    let returnArray: any[] = [];

    // does an initial shuffle of the array
    returnArray = this.utils.shuffleRepetitions(valueTypeArray, 1);
    let copyArray = returnArray.slice();

    // ensures that no consecutive duplicates are in the array
    returnArray = this.utils.shuffleStimuli(copyArray);

    return returnArray;
  }
}
